import type { MediaProgress } from 'room/common/media-progress'

/**
 * How the console's video monitor stays with the film on the projector. The
 * presentation window reports where its player is (`MediaProgress`); the
 * monitor is a second player that only ever follows. These are the rules
 * for one look at the two, pure so they are tested without a player.
 */

/** Further apart than this and the monitor jumps; less is not worth a jump. */
export const MONITOR_DRIFT_MS = 1500

/**
 * A film this close to its end is left to finish: while the monitor sits at
 * the end and the report still says playing, "play" would show the first
 * frame again until the projector's end reaches the console.
 */
export const END_MARGIN_MS = 1000

export type MonitorCorrection =
	| { type: 'none' }
	| { type: 'play' }
	| { type: 'pause' }
	| { type: 'seek'; positionMs: number }

const NONE: MonitorCorrection = { type: 'none' }
const PLAY: MonitorCorrection = { type: 'play' }
const PAUSE: MonitorCorrection = { type: 'pause' }

/**
 * Where the projector's player is by now: its report, moved on by the time
 * since while it plays, and never past the end.
 */
export const expectedPositionMs = (
	remote: MediaProgress,
	now: number
): number => {
	const moved = remote.playing ? Math.max(0, now - remote.at) : 0
	const position = remote.positionMs + moved
	return remote.durationMs > 0
		? Math.min(position, remote.durationMs)
		: position
}

type Look = {
	/** The projector's last report; `null` once its player is gone. */
	remote: MediaProgress | null
	localPositionMs: number
	/** Playing or about to (buffering): the monitor is not stopped. */
	localPlaying: boolean
	/** `Date.now()`, the clock `remote.at` was read from. */
	now: number
}

/**
 * What the monitor's player should do next, if anything: with no report it
 * stops; too far from the projector it jumps (to where the projector is by
 * now); otherwise it takes the projector's play or pause.
 */
export const monitorCorrection = ({
	remote,
	localPositionMs,
	localPlaying,
	now,
}: Look): MonitorCorrection => {
	if (!remote) {
		return localPlaying ? PAUSE : NONE
	}
	const expected = expectedPositionMs(remote, now)
	if (Math.abs(localPositionMs - expected) > MONITOR_DRIFT_MS) {
		return { type: 'seek', positionMs: Math.round(expected) }
	}
	if (remote.playing === localPlaying) {
		return NONE
	}
	if (!remote.playing) {
		return PAUSE
	}
	const atTheEnd =
		remote.durationMs > 0 && expected >= remote.durationMs - END_MARGIN_MS
	return atTheEnd ? NONE : PLAY
}
